import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, EditPane, Field, Input, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { FilePlus2, Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PUBLISHER_METADATA_FIELDS,
  PUBLISHER_SPLIT_MODES,
  useApi,
  type PublisherDTO,
  type PublisherMetadataTemplateDTO,
  type PublisherMetadataTemplateFieldMappingDTO,
} from "../../api";
import { PUBLISHER_STATUS_VARIANT, PUBLISHER_TYPE_VARIANT } from "../../components/badges";
import { EnumBadge, percent } from "../../components/cells";
import { crudEditor, numberField, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * One publisher and its metadata template.
 *
 * The template — how the publisher's own spreadsheet maps onto artwork fields —
 * used to be a page of its own, listing every publisher's template in one
 * grid. There is exactly one per publisher, which is what lets the artwork
 * import pick a template from the publisher alone, so it belongs here, the way
 * a client's addresses sit on the client. The publisher's own fields are edited
 * from the list, as before; this page is the template.
 *
 * The template keeps its own view code: an account allowed to see publishers
 * but not their templates gets the header and nothing under it.
 */
export function PublisherDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams({ from: "/config/publisher/$id" });
  const publisherId = Number(id);
  const allowed = useAllowed(LogicView.CONFIG_PUBLISHER);
  const canTemplate = useAllowed(LogicView.CONFIG_PUBLISHER_METADATA_TEMPLATES);
  const title = t("navigation.item.publishers", "Publishers");

  const publisher = useQuery({
    queryKey: ["publisher", publisherId],
    queryFn: () => api.publishers.findById(publisherId),
    enabled: allowed && Number.isFinite(publisherId),
  });

  const template = useQuery({
    queryKey: ["metadata-template", "publisher", publisherId],
    queryFn: () => api.metadataTemplates.findByPublisherId(publisherId),
    enabled: allowed && canTemplate && Number.isFinite(publisherId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["metadata-template"] });
  };

  const entity = publisher.data;
  if (!allowed) return <AccessDenied title={title} />;
  if (publisher.error) return <ErrorState title={title} error={publisher.error} />;
  if (publisher.isSuccess && !entity) {
    void navigate({ to: "/config/publisher" });
    return null;
  }

  const mappings = template.data?.field_mappings ?? [];

  return (
    <>
      <PageHeader
        parent={{ to: "/config/publisher", label: title }}
        title={entity ? `${entity.code} - ${entity.name}` : `#${publisherId}`}
        badges={
          entity ? (
            <div className="flex items-center gap-1.5">
              <EnumBadge value={entity.type} variants={PUBLISHER_TYPE_VARIANT} />
              <EnumBadge value={entity.status} variants={PUBLISHER_STATUS_VARIANT} />
              <Badge variant="neutral">
                {t("column.royalty", "Royalty")} {percent(entity.royalty)}
              </Badge>
            </div>
          ) : undefined
        }
      />

      <EditPane
        header={
          canTemplate && entity ? (
            <TemplateSummary publisher={entity} template={template.data ?? null} onChanged={refresh} />
          ) : undefined
        }
        tabs={[
          {
            id: "field-mappings",
            flush: true,
            hidden: !canTemplate,
            label: t("tab.field-mappings", "Field mappings"),
            badge: <Badge size="sm">{mappings.length}</Badge>,
            content: template.data ? (
              <FieldMappingsGrid template={template.data} onChanged={refresh} />
            ) : (
              <div className="flex min-h-64 items-center justify-center p-6 text-[13px] text-ink-muted">
                {template.isLoading
                  ? ""
                  : t("publisher.template.none", "This publisher has no metadata template yet.")}
              </div>
            ),
          },
        ]}
      />
    </>
  );
}

/* ------------------------------------------------------------ template --- */

/**
 * The template's own two fields, above the mappings: its name and the row of
 * the spreadsheet the column headers are on. Create when there is none, edit
 * or delete when there is — the same three operations the old list offered,
 * without the publisher picker, since the publisher is the page.
 */
function TemplateSummary({
  publisher,
  template,
  onChanged,
}: {
  publisher: PublisherDTO;
  template: PublisherMetadataTemplateDTO | null;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const { canWrite } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ name: string; header_row: string }>({ name: "", header_row: "1" });

  const open = () => {
    setDraft({ name: template?.name ?? publisher.name, header_row: String(template?.header_row ?? 1) });
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const body: PublisherMetadataTemplateDTO = {
        id: template?.id ?? 0,
        publisher_id: publisher.id,
        name: draft.name.trim(),
        header_row: Number(draft.header_row),
        field_mappings: template?.field_mappings ?? [],
      };
      return template ? api.metadataTemplates.update(body) : api.metadataTemplates.create(body);
    },
    onSuccess: async () => {
      toast.success(t("notification.save.success", "Saved"));
      setEditing(false);
      await onChanged();
    },
    onError: (failure) => toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const remove = async () => {
    if (!template) return;
    const ok = await confirm({
      title: t("dialog.confirm.delete.title", "Delete"),
      message: t(
        "publisher.template.delete.text",
        "Delete this metadata template and its {{count}} field mappings?",
        { count: template.field_mappings?.length ?? 0 },
      ),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.metadataTemplates.remove(template.id);
      toast.success(t("notification.delete.success", "Deleted"));
      await onChanged();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
    }
  };

  const valid = draft.name.trim().length > 0 && Number(draft.header_row) >= 1;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col leading-tight">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
          {t("publisher.template", "Metadata template")}
        </span>
        <span className="text-[13px] text-ink">
          {template
            ? `${template.name} · ${t("column.header-row", "Header row")} ${template.header_row}`
            : t("publisher.template.none", "This publisher has no metadata template yet.")}
        </span>
      </div>
      {canWrite && (
        <div className="ml-auto flex items-center gap-2">
          {template ? (
            <>
              <Button size="sm" variant="secondary" icon={<Pencil className="size-3.5" />} onClick={open}>
                {t("button.edit", "Edit")}
              </Button>
              <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => void remove()}>
                {t("button.delete", "Delete")}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="primary" icon={<FilePlus2 className="size-3.5" />} onClick={open}>
              {t("publisher.template.create", "Create template")}
            </Button>
          )}
        </div>
      )}

      <Dialog
        open={editing}
        onClose={() => setEditing(false)}
        title={t("publisher.template", "Metadata template")}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
              {t("button.save", "Save")}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label={t("column.name", "Name")} required>
            <Input autoFocus value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          </Field>
          <Field label={t("column.header-row", "Header row")} required>
            <Input
              type="number"
              min={1}
              value={draft.header_row}
              onChange={(event) => setDraft({ ...draft, header_row: event.target.value })}
            />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------- mappings --- */

/**
 * The column-header → artwork-field mapping for the template.
 *
 * `split_mode` is what lets a single "Colours" column become three colour tags:
 * COMMA_SEPARATED splits the cell, SINGLE takes it whole.
 */
function FieldMappingsGrid({
  template,
  onChanged,
}: {
  template: PublisherMetadataTemplateDTO;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const rows = template.field_mappings ?? [];

  const cols = useMemo(() => {
    const c = defineColumns<PublisherMetadataTemplateFieldMappingDTO>();
    return [
      c.text("column_header", (row) => row.column_header, {
        headerKey: "column.column-header",
        priority: 1,
      }),
      c.text("field", (row) => row.field, {
        headerKey: "column.field",
        filter: "select",
        priority: 1,
      }),
      c.text("split_mode", (row) => row.split_mode, {
        headerKey: "column.split-mode",
        filter: "select",
        priority: 3,
      }),
      c.number("sort_order", (row) => row.sort_order, {
        headerKey: "column.sort-order",
        priority: 4,
      }),
    ];
  }, []);

  const editor = useMemo(
    () =>
      crudEditor<PublisherMetadataTemplateFieldMappingDTO>({
        client: api.metadataFieldMappings,
        refresh: onChanged,
        t,
        fields: {
          column_header: textField(
            (row) => row.column_header,
            (row, value) => ({ ...row, column_header: value }),
            true,
          ),
          field: selectField(
            (row) => row.field,
            (row, value) => ({
              ...row,
              field: value as PublisherMetadataTemplateFieldMappingDTO["field"],
            }),
            PUBLISHER_METADATA_FIELDS,
          ),
          split_mode: selectField(
            (row) => row.split_mode,
            (row, value) => ({
              ...row,
              split_mode: value as PublisherMetadataTemplateFieldMappingDTO["split_mode"],
            }),
            PUBLISHER_SPLIT_MODES,
          ),
          sort_order: numberField(
            (row) => row.sort_order,
            (row, value) => ({ ...row, sort_order: value === "" ? 0 : Number(value) }),
          ),
        },
        newRow: () => ({
          id: 0,
          template_id: template.id,
          column_header: "",
          field: "CODE",
          split_mode: "SINGLE",
          sort_order: rows.length,
        }),
        validate: (row) => {
          const errors: Record<string, string> = {};
          const required = t("form.validation.field.required", "Value is required");
          if (!row.column_header.trim()) errors.column_header = required;
          if (!row.field) errors.field = required;
          if (!row.split_mode) errors.split_mode = required;
          return Object.keys(errors).length > 0 ? errors : null;
        },
      }),
    [api, onChanged, rows.length, t, template.id],
  );

  return (
    <DataTable<PublisherMetadataTemplateFieldMappingDTO>
      data={rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      editor={editor}
      initialSorting={[{ id: "sort_order", desc: false }]}
    />
  );
}
